/**
* Component: Attachments.jsx
* Description: This component is upload and download the attachments also it handles authorization token
*/

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BemsGraph, FemsGraph, MasterGraph } from "../../utility/apiURLS";
import { getAuthToken } from "../../utility/tokenUtility";
import BottomButtons from './BottomButtons';
import ComponentHeader from './ComponentHeader';
import { useMediaQuery } from '@mui/material';
import assetCreateUpdateRepository from '../../api/repositories/assetCreateUpdateRepository';
import { gridValues } from '../../redux/actions/assetActions';
import CustomAgGrid from './CustomAgGrid';
import { randomId } from '@mui/x-data-grid-generator';
import CustomAlert from '../userlocationcomponents/CustomAlert';
import CustomTooltip from './CustomTooltip';
import { useAccess } from '../../AccessContext';
import utilities from '../../utility/utilities';

//function to add new row to the grid
function AddRowButton(props) {
  const handleClick = () => {
    props.onClick();
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={handleClick}>
        Add Row
      </Button>
    </Box>
  );
}

export default function Attachment({ setPage, page, buttonAttributes, setAuthourizeTick }) {

  const accessDataFem = useAccess();
  const accessFem = utilities.getScreenPermissions(accessDataFem, "ENGM013");
  const accessDataBem = useAccess();
  const accessBem = utilities.getScreenPermissions(accessDataBem, "ENGM014");

  //getting selected grid data
  const selectedGridData = useSelector(
    (state) => state.gridValues.selectedGridData
  );

  //getting basic details from redux
  const basicDetails = useSelector((state) => state.basicDetails);

  //get the purchase details form redux
  const purchaseDetails = useSelector(
    (state) => state?.purchaseDetails
  );

  //variable declarations
  const dispatch = useDispatch();

  const initialRow = {
    id: randomId(),
    file: '',
    fileName: '', // Initialize with empty string
    fileType: '', // Initialize with empty string
    size: '',     // Initialize with empty string
  };

  //state declarations
  const [openDialog, setOpenDialog] = useState(false);
  const [enableAuthorizeButton, setEnableAuthorizeButton] = useState(false);
  const [rows, setRows] = useState([initialRow]);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedfiles, setSelectedFiles] = useState([]);
  const fileExtentions = ['jpeg', 'jpg', 'png', 'xlsx', 'csv', 'pdf', 'doc'];
  const [gridApi, setGridApi] = useState(null);
  const [openAuthorizeDialog, setOpenAuthorizeDialog] = useState(false);
  const [confirmAuthorizeDialog, setConfirmAuthorizeDialog] = useState(false);
  const assetCategoryType = useSelector((state) => state?.serviceType?.assetType);
  const assetType = assetCategoryType === "/assetsFEM" ? FemsGraph : BemsGraph;
  const findHeaderContent = buttonAttributes.find((item) => item.currentPageName === page);
  const [isAnyFilesAffected, setIsAnyFilesAffected] = useState(false);
  const [getByIdFiles, setGetByIdFiles] = useState([]);
  const [storeFileName, setStoreFileName] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [deleteConfrimDialoug, setDeleteConfrimDialoug] = useState(false);
  const [currentId, setCurrentId] = useState('');

  const handleAddRow = () => {
    const id = randomId();
    setRows((oldRows) => [
      ...oldRows,
      {
        id,
        file: '',
        fileName: '', // Initialize with empty string
        fileType: '', // Initialize with empty string
        size: '',     // Initialize with empty string
      },
    ]);
  };

  //get the attachments from api
  const fetchFiles = async () => {
    try {
      const id = selectedGridData?.values?.id || basicDetails?.id;
      setIsLoading(true)
      const config = {
        headers: {
          Authorization: `Bearer ${getAuthToken('id_token')}`
        }
      }
      const attachment = await axios.get(`${assetType}/api/GraphAssetregister/GetAssetRegisterAttachmentsList/${id}`, config);
      setGetByIdFiles(attachment?.data);
      //if the uploaded file in redux then set the uploaded file in grid
      if (attachment?.data?.length > 0) {
        const updatedRows = attachment?.data?.map((item) => {
          
          const filenameWithoutExtension = item?.documenttitle?.split('.').slice(0, -1).join('.');
          const fileExtension = item?.documenttitle?.split('.').pop();
          // const size = (parseFloat(item?.filesize?.split(' ')[0]) / 1024).toFixed(2) + ' MB';
          const size = item?.filesize;
          return {
            id: item?.id,
            file: item,
            fileName: filenameWithoutExtension,
            fileType: fileExtension?.toUpperCase(),
            size: size
          };
        });
        setRows(updatedRows);
      } else {
        setRows([initialRow]);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsAnyFilesAffected(false);
      setIsLoading(false)
    }
  };

  useEffect(() => {
    setEnableAuthorizeButton(true);
  }, [])

  useEffect(() => {
    fetchFiles();
  }, [isAnyFilesAffected]); //when ever isAnyFilesAffected changes then fetchOptions will be called

  //function to handle file change
  const handleFileChange = (event, id) => {
    const file = event.target.files[0];
    if (!file) return;
    const fileName = file?.name;
    const fileExtension = fileName?.split('.')?.pop();

    if (file?.size > (20 * 1024 * 1024)) {
      setError(true);
      setErrorMessage("File size exceeds 20 MB. Please select smaller files.");
      return;
    }

    if (!fileExtentions?.includes(fileExtension)) {
      setError(true);
      setErrorMessage("Only JPEG, PDF, JPG, PNG, CSV, and XLSX files are allowed.");
      return;
    }

    const fileNameWithoutExtension = fileName?.replace('.' + fileExtension, '');

    const selectedFiles = [...selectedfiles, file];
    setSelectedFiles(Array.from(selectedFiles));
    const updatedRows = rows?.map((row) => {
      if (row?.id === id) {
        return {
          ...row,
          file,
          fileName: fileNameWithoutExtension,
          fileType: fileExtension.toUpperCase(),
          size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
        };
      }
      return row;
    });

    setRows(updatedRows);
    setIsDataChanged(true);
  };

  const checkConfrimDelete = (row) => {
    const { id, fileName } = row;
    if (id && fileName) {
      setDeleteConfrimDialoug(true);
      setCurrentId(id);
    } else {
      handleDeleteClick(id);
    }
  };

  const handleDeleteClick = async (id) => {
    const config = {
      headers: {
        Authorization: `Bearer ${getAuthToken('id_token')}`
      }
    };
    const isFileStored = getByIdFiles?.find((item) => item?.id === id);

    if (rows?.length > 0 && isFileStored) {
      try {
        const serviceType = assetCategoryType === "/assetsFEM" ? "FEMS" : "BEMS";
        const url = serviceType === "FEMS" ? FemsGraph : BemsGraph;
        const femorbem = serviceType === "FEMS" ? "femassetattachments" : "bemassetattachments";
        const response = await axios.delete(`${url}/api/${femorbem}/${id}`, config);
        if (response.status >= 200 && response.status <= 204) {
          const updatedRows = rows.filter((row) => row?.id !== id);
          setRows(updatedRows);
          setIsAnyFilesAffected(true);
        }
      } catch (error) {
        console.log('Error deleting attachment:', error);
        setError(true);
        setErrorMessage('Error deleting attachment. Please try again.');
      }
    } else if (rows?.length > 1) {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      const deletedRow = rows.find((row) => row.id === id);
      const fileToDelete = deletedRow?.file;
      setSelectedFiles(updatedRows);
    } else if (rows?.length === 1) {
      const index = rows?.findIndex((item) => item?.id === id);
      if (!rows[index]?.fileName) {
        return; // Do nothing
      } else {
        setRows([initialRow]);
      }
      setSelectedFiles([])
    }
  };



  //function to hanlde download the file. if the file is stored then it will download from db otherwise it will download from grid
  const handleDownload = async (file) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${getAuthToken('id_token')}`
        }
      }
      const isFileStored = getByIdFiles?.find((item) => item?.id === file?.id)
      if ((file && file?.filename || storeFileName?.length > 0) && isFileStored) { //this will handle edit time or store time download
        const fileName = file?.filename;
        const response = await axios.get(`${MasterGraph}/api/FileUpload/DownloadFile/${fileName}`, {
          ...config,
          responseType: 'blob'
        });
        if (response) {
          const url = URL.createObjectURL(response.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = file?.documenttitle;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } else { //this will handle initial time download
        if (file) {
          // convert the file to blob
          const fileToDownload = file?.file;
          if (fileToDownload) {
            const blob = new Blob([fileToDownload], { type: fileToDownload.type });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileToDownload.name || 'downloaded_file';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } else {
            console.log('Invalid file object:', file);
          }
        }
      }
    } catch (error) {
      console.log('Error downloading file:', error);
    }
  };


  // function to handle authorize click make the filed isAuthorized to true and updates in api
  const handleAuthorizeClick = async () => {
    try {
      //make the isAuthorized true
      let requestBody = {
        isauthorized: true, // Example: true
      };
      const type = assetType === FemsGraph ? "FEMS" : "BEMS";
      const selectedData = selectedGridData?.values;
      const response = await assetCreateUpdateRepository?.updateAsset(selectedData, requestBody, type);
      if (response?.id && response?.isauthorized) {
        setEnableAuthorizeButton(true);
        setOpenAuthorizeDialog(true);
        dispatch(gridValues({ values: response, isEdit: true, isView: false }));
      } else if (response.status === 401) {
        console.log("Unauthorized");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("data not inserted properly")
      } else {
        console.log("data not inserted properly")
      }
    }
  };

  //function to generate unique id for each file with extension
  function uuidv4(extension) {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    }) + '.' + extension;
  };

  //function to save files to attachment with respective tables after the file is registered to mastergraph
  const saveFilesToAttachment = async (serviceType, payload) => {
    const femorbem = serviceType === "FEMS" ? "femassetattachments" : "bemassetattachments";
    const config = {
      headers: {
        Authorization: `Bearer ${getAuthToken('id_token')}`
      }
    };

    try {
      const response = await axios.post(`${assetType}/api/${femorbem}`, payload, config);
      return response?.data;
    } catch (error) {
      console.log('saveFilesToAttachment Save error:', error);
    }
  };

  //function to register files to mastergraph
  //files need to register here before saving to respective attachment table
  const registerFiles = async () => {
    const token = getAuthToken('id_token');
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };

    const selectedFiles = selectedfiles;
    let storeFileNameArray = [];
    let storeregsitredFilesResponse = [];
    let storefileUploadResponse = [];

    for (let i = 0; i < selectedFiles?.length; i++) {
      const formData = new FormData();
      const extension = selectedFiles[i]?.name.split('.').pop();
      const fileName = uuidv4(extension); // generate a unique id
      formData?.append('fileObj', selectedFiles[i], fileName); // append file with unique id as filename
      storeFileNameArray.push({ id: fileName, filename: `${selectedFiles[i]?.name}`, selectedFileId: selectedFiles[i]?.id });

      try {
        const fileUploadResponse = await axios.post(`${MasterGraph}/api/fileupload`, formData, config);
        storeregsitredFilesResponse.push(fileUploadResponse.data);
        storefileUploadResponse.push(fileUploadResponse.data);
      } catch (error) {
        console.log('Error uploading file:', error.response ? error.response.data : error.message);
      }
    }
    setStoreFileName(storeFileNameArray);
    return { storefileUploadResponse, storeFileNameArray };
  };


  //function to handle files to register and saved
  const handleSaveClick = async () => {
    if (selectedGridData?.values?.isauthorized) {
      setEnableAuthorizeButton(true);
    } else {
      setEnableAuthorizeButton(false);
    }
    try {
      if (selectedfiles?.length > 0) {
        const { storefileUploadResponse, storeFileNameArray } = await registerFiles();
        const serviceType = assetType === FemsGraph ? "FEMS" : "BEMS";
        const femorbem = serviceType === "FEMS" ? "femassetregisterId" : "bemassetregisterId";
        const assetId = selectedGridData?.values?.id || basicDetails?.id;
        if (storefileUploadResponse && storefileUploadResponse.length > 0) {
          let storefileUploadResponseFinal = [];
          for (let i = 0; i < storefileUploadResponse?.length; i++) {
            const file = storefileUploadResponse[i];
            const documentTitle = storeFileNameArray.find((item) => item.id === file.fileName)?.filename;

            if (documentTitle) {


              const payload = {
                id: "",
                filename: file.fileName,
                filesize: file.fileSize,
                filepath: "D:/IFAC-X/UploadFolder",
                [femorbem]: assetId,
                documenttitle: documentTitle,
                isdeleted: false,
              };
              try {
                const response = await saveFilesToAttachment(serviceType, payload);
                if (response) {
                  storefileUploadResponseFinal.push(response);
                } else {
                  console.log("Error saving file:", file.fileName);
                }
              } catch (error) {
                console.log('Error saving file:', error);
              }
            } else {
              console.log("Document Title not found for file:", file.fileName);
            }
          }
          if (storefileUploadResponseFinal?.length > 0 && storefileUploadResponseFinal?.length === storefileUploadResponse?.length) {
            const type = assetType === FemsGraph ? "FEMS" : "BEMS";
            const response = await assetCreateUpdateRepository?.updateAsset(selectedGridData?.values, { processingpercentage: 100 }, type);
            if (response) {
              dispatch(gridValues({ values: response, isEdit: true, isView: false }));
              setSelectedFiles([]);
              setOpenDialog(true);
              setIsAnyFilesAffected(true);
            }
          } else {
            console.log("Error in uploading some files");
          }
        } else {
          console.log("No files registered");
        }
      } else {
        const type = assetType === FemsGraph ? "FEMS" : "BEMS";
        const response = await assetCreateUpdateRepository?.updateAsset(selectedGridData?.values, { processingpercentage: 100 }, type);
        if (response) {
          setOpenDialog(true);
          dispatch(gridValues({ values: response, isEdit: true, isView: false }));
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  const handleCloseDialog = () => {
    setAuthourizeTick(1);
    setOpenDialog(false);
  };

  const handleDownloadClick = (event, row) => {
    event.preventDefault(); // Prevent default link behavior (redirect)
    handleDownload(row?.file, row?.id);
  };

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const assetValue = basicDetails?.assetValue || selectedGridData?.values?.assetNo || '';

  const handleAuthorize = () => {
    setConfirmAuthorizeDialog(true);
  }

  return (
    <>
      <form>
        <ComponentHeader
          requiredButton={findHeaderContent.requiredButton}
          mainHeader={findHeaderContent.mainHeader}
          assetvalue={assetValue}
          subHeader={findHeaderContent.subHeader}
          statusType={selectedGridData?.values?.isauthorized ? 'authorized' : 'unauthorized'}
        />
        <Grid container direction="column" style={{ width: '100%' }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <AddRowButton onClick={handleAddRow} />
          </Grid>
          <CustomAgGrid
            rows={rows}
            isLoading={isLoading}
            columns={[
              {
                field: 'file',
                headerName: 'File',
                flex: 1,
                minWidth: isSmallScreen ? 150 : 200,
                cellRenderer: (params) => (
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    sx={{
                      width: '100px',
                      height: '38px',
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                    disabled={!!params?.data?.fileName}
                  >
                    {params?.data?.fileName ? 'Uploaded' : 'Upload'}
                    <input
                      type="file"
                      hidden
                      onChange={(event) => handleFileChange(event, params?.data?.id)}
                    />
                  </Button>
                ),
              },
              {
                field: 'fileName',
                headerName: 'File Name',
                flex: 1,
                minWidth: isSmallScreen ? 80 : 120,
              },
              {
                field: 'fileType',
                headerName: 'File Type',
                flex: 1,
                minWidth: isSmallScreen ? 80 : 120,
              },
              {
                field: 'size',
                headerName: 'Size',
                flex: 1,
                minWidth: isSmallScreen ? 80 : 120,
              },
              {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                flex: 1,
                minWidth: 160,
                cellClassName: 'actions',
                cellRenderer: (params) => (
                  <>
                   <CustomTooltip title="Delete">
                      <Button onClick={() => checkConfrimDelete(params?.data)} disabled = {(rows?.length === 1 && params?.data?.id && !params?.data?.fileName) ? true : false }>
                        <DeleteIcon />
                      </Button>
                  </CustomTooltip>
                  {params?.data?.file?.filename && (
                  <CustomTooltip title="Download">
                      <Button onClick={(event) => handleDownloadClick(event, params.data)}>
                        <DownloadIcon />
                      </Button>
                  </CustomTooltip>
                    )}
                  </>
                ),
              }
            ]}
            setGridApi={setGridApi}
          />
        </Grid>

        <BottomButtons isDataChanged={isDataChanged} page={page} enableAuthorizeButton={enableAuthorizeButton} buttonAttributes={buttonAttributes} authorizeButton={accessFem?.canAuthorize || accessBem?.canAuthorize} continueButton={accessFem?.canAuthorize || accessBem?.canAuthorize} saveButton={true} saveClick={() => handleSaveClick()}  authorizeClick={() => handleAuthorize()} />

        <CustomAlert
          open={confirmAuthorizeDialog}
          onClose={() => setConfirmAuthorizeDialog(false)}
          onConfirm={() => {
            handleAuthorizeClick()
            setConfirmAuthorizeDialog(false)
          }}
          content={`Are you sure you want to authorize this asset (${selectedGridData?.values?.assetno})?`}
          confirmBtn="Yes"
          cancelBtn="No"
        />

        {/* Dialog for the finish action */}
        <CustomAlert
          open={openAuthorizeDialog}
          onClose={() => setOpenAuthorizeDialog(false)}
          onConfirm={() => setOpenAuthorizeDialog(false)}
          content={`Asset (${selectedGridData?.values?.assetno}) has been authorized successfully`}
          confirmBtn="Ok"
        />
        <CustomAlert
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          content={purchaseDetails?.isAssetCreation ? `Asset (${selectedGridData?.values?.assetno}) has been created successfully` : `Asset (${selectedGridData?.values?.assetno}) has been updated successfully`}
          confirmBtn="Ok"
        />
        <CustomAlert
          open={!!errorMessage}
          onClose={() => setErrorMessage("")}
          onConfirm={() => setErrorMessage("")}
          content={errorMessage}
          cancelBtn="Cancel"
          confirmBtn="Ok"
        />
        <CustomAlert
          open={deleteConfrimDialoug}
          onClose={() => {
            setDeleteConfrimDialoug(false)
            setCurrentId('');
          }}
          onConfirm={() => {
            handleDeleteClick(currentId);
            setDeleteConfrimDialoug(false)
            setCurrentId('')
          }}
          content={"Are you sure want to delete?"}
          cancelBtn={"NO"}
          confirmBtn={"YES"}
        />
      </form>
    </>
  );
}